import { ActionIcon, AppShell, Burger, Button, Divider, Drawer, Flex, Stack } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useNavigate } from 'react-router';
import { FullLogoIcon } from 'src/UI/icons';
import SquareLogo from 'src/UI/icons/logo/SquareLogo';
import classes from './HeaderRevamp.module.css';
import { useIsMobile } from 'src/common/hooks/useIsMobile';
import { useIsAfternoon } from 'src/common/useIsAfternoonHook';
import { Logger } from 'src/utils/Logger';
import { useLogout } from 'src/auth/hooks/useLogout';
import { showError } from 'src/toasts/toasts';
import { CLIENT_TABS_REGEX } from 'src/home/ClientData';
import { IconLogout, IconMoodEdit, IconSettings, IconSun } from '@tabler/icons-react';
import { useEffect } from 'react';
import useCurrentUser from 'src/admin/users/hooks/useCurrentUser';
import { CreateButton } from 'src/common/components/CreateButton';
import PinnedFlagsPopover from 'src/feature-flags/PinnedFlagsPopover';
import { useFlags } from 'src/feature-flags/FlagsHook';
import NotificationBell from 'src/notifications/components/NotificationBell';
import {
  DrawerActionItem,
  DrawerNavItem,
  HeaderNavItem,
  HeaderNavLink,
  UserAvatarMenu,
} from './HeaderNavigation';

export const HEADER_HEIGHT = 72;

export default function Header({
  showChat,
  isChatOpened,
  bg = 'navBackground',
  showSiestaButton = true,
}: {
  showChat: () => void;
  isChatOpened: boolean;
  bg?: string;
  showSiestaButton?: boolean;
}) {
  const navigate = useNavigate();
  const { isCrawford, isAfternoonAdmin } = useIsAfternoon();
  const { currentUser, isSystemAdmin, isServiceAdmin } = useCurrentUser();
  const { mutate: logout } = useLogout({
    onError: error => {
      Logger.error('Log out failed', error);
      showError('Something went wrong while trying to log you out. Please try again.');
    },
  });
  const {
    flagEnabledResponsiveMenus,
    flagEnabledAdminAdviserSignatures,
    flagEnabledNotifications,
    flagEnabledHomeDashboard,
  } = useFlags();
  const showUserManagement = isSystemAdmin || (isServiceAdmin && flagEnabledAdminAdviserSignatures);
  const { isMobile } = useIsMobile();
  const shouldCollapse = isMobile && flagEnabledResponsiveMenus;
  const [drawerOpened, { open: openDrawer, close: closeDrawer }] = useDisclosure(false);

  useEffect(() => {
    if (!shouldCollapse && drawerOpened) closeDrawer();
  }, [shouldCollapse, drawerOpened, closeDrawer]);

  const navItems: HeaderNavItem[] = [
    ...(flagEnabledHomeDashboard
      ? [{ title: 'Home', href: '/dashboard', activeRegex: /^\/dashboard$/ }]
      : []),
    { title: 'All clients', href: '/clients/all', activeRegex: CLIENT_TABS_REGEX },
    { title: 'Company data', href: '/company', activeRegex: /^\/company\/.*$/ },
    { title: 'Meetings', href: '/meetings', activeRegex: /^\/meetings\/.*$/ },
  ];

  const goToAndClose = (href: string) => {
    closeDrawer();
    navigate(href);
  };

  const handleSiestaFromDrawer = () => {
    closeDrawer();
    showChat();
  };

  return (
    <AppShell.Header withBorder={false} className={classes.headerContainer} bg={bg}>
      {shouldCollapse ? (
        <Flex align="center" h="100%" px="md" justify="space-between">
          <Flex h="100%" align="center" gap="sm">
            <Burger
              opened={drawerOpened}
              onClick={openDrawer}
              color="white"
              size="sm"
              aria-label="Open navigation"
            />
            <NavigableLogo />
          </Flex>

          <Flex align="center" h="100%" gap="sm">
            {showSiestaButton && (
              <ActionIcon
                variant="transparent"
                size={42}
                onClick={showChat}
                aria-label={isChatOpened ? 'Close Siesta AI' : 'Ask Siesta AI'}
              >
                <img src="/images/sparkle-icon.png" style={{ width: '40px', height: '40px' }} />
              </ActionIcon>
            )}
            {flagEnabledNotifications && <NotificationBell />}
            <UserAvatarMenu
              currentUser={currentUser}
              isCrawford={isCrawford}
              isSystemAdmin={isSystemAdmin}
              isServiceAdmin={isServiceAdmin}
              onNavigate={navigate}
              onLogout={() => logout()}
            />
          </Flex>
        </Flex>
      ) : (
        <Flex align="center" h="100%" px="xxl" justify="space-between">
          <Flex h="100%" align="center" gap={{ base: 'xxl', lg: 'xxxl', xl: 44 }}>
            <NavigableLogo />
            <Flex align="center" h="100%" gap="sm">
              {navItems.map(item => (
                <HeaderNavLink key={item.href} {...item} />
              ))}
            </Flex>
          </Flex>

          <Flex align="center" h="100%" gap="xxl">
            <Flex align="center" h="100%" gap="sm">
              {isAfternoonAdmin && <PinnedFlagsPopover onClick={() => navigate('/secret-menu')} />}
              <CreateButton buttonProps={{ h: 40, variant: 'filled-pill', bg: 'brand.6' }} />
              {showSiestaButton && (
                <Button
                  h={40}
                  miw={165}
                  justify="start"
                  variant="filled-pill"
                  bg={isChatOpened ? 'gray.3' : 'white'}
                  onClick={showChat}
                  leftSection={
                    <img
                      src="/images/sparkle-icon.png"
                      style={{ width: '20px', height: '20px', borderRadius: '50%' }}
                    />
                  }
                >
                  {isChatOpened ? 'Close Siesta AI' : 'Ask Siesta AI'}
                </Button>
              )}
            </Flex>

            {flagEnabledNotifications && <NotificationBell />}
            <UserAvatarMenu
              currentUser={currentUser}
              isCrawford={isCrawford}
              isSystemAdmin={isSystemAdmin}
              isServiceAdmin={isServiceAdmin}
              onNavigate={navigate}
              onLogout={() => logout()}
            />
          </Flex>
        </Flex>
      )}

      {shouldCollapse && (
        <Drawer.Root opened={drawerOpened} onClose={closeDrawer} size="100%" position="left">
          <Drawer.Overlay />
          <Drawer.Content style={{ display: 'flex', flexDirection: 'column' }}>
            <Drawer.Header>
              <Drawer.Title>
                <LogoWithIcon textColor="var(--mantine-color-black)" />
              </Drawer.Title>
              <Flex align="center" gap="xxs">
                {isAfternoonAdmin && (
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    size={28}
                    onClick={() => goToAndClose('/secret-menu')}
                    aria-label="Secret menu"
                    style={{ opacity: 0.6 }}
                  >
                    <IconSun stroke={1} size={20} />
                  </ActionIcon>
                )}
                <Drawer.CloseButton />
              </Flex>
            </Drawer.Header>
            <Drawer.Body
              style={{
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                overflowY: 'auto',
                minHeight: 0,
              }}
            >
              <Stack gap="xs" style={{ flex: 1 }}>
                {navItems.map(item => (
                  <DrawerNavItem
                    key={item.href}
                    {...item}
                    onSelect={() => goToAndClose(item.href)}
                  />
                ))}
              </Stack>

              <Stack gap="sm" pt="md">
                <Divider />
                <CreateButton
                  buttonProps={{
                    h: 44,
                    variant: 'filled-pill',
                    bg: 'brand.6',
                    fullWidth: true,
                    justify: 'center',
                  }}
                />
                {showSiestaButton && (
                  <Button
                    h={44}
                    fullWidth
                    justify="center"
                    variant="filled-pill"
                    bg={isChatOpened ? 'gray.3' : 'gray.1'}
                    c="black"
                    onClick={handleSiestaFromDrawer}
                    leftSection={
                      <img
                        src="/images/sparkle-icon.png"
                        style={{ width: '20px', height: '20px', borderRadius: '50%' }}
                      />
                    }
                  >
                    {isChatOpened ? 'Close Siesta AI' : 'Ask Siesta AI'}
                  </Button>
                )}

                <Divider label={`Welcome, ${currentUser?.firstName ?? ''}`} labelPosition="left" />

                <DrawerActionItem
                  icon={<IconMoodEdit size={18} />}
                  label="My details"
                  onClick={() => goToAndClose(`/user/details/${currentUser?.id}`)}
                />
                {showUserManagement && (
                  <DrawerActionItem
                    icon={<IconSettings size={18} />}
                    label="User management"
                    onClick={() => goToAndClose('/admin/users')}
                  />
                )}
                {isSystemAdmin && (
                  <DrawerActionItem
                    icon={<IconSettings size={18} />}
                    label="Data export configuration"
                    onClick={() => goToAndClose('/admin/data-export-config')}
                  />
                )}
                <DrawerActionItem
                  icon={<IconLogout size={18} />}
                  label="Log out"
                  onClick={() => {
                    closeDrawer();
                    logout();
                  }}
                />
              </Stack>
            </Drawer.Body>
          </Drawer.Content>
        </Drawer.Root>
      )}
    </AppShell.Header>
  );
}

function NavigableLogo() {
  const navigate = useNavigate();
  return <LogoWithIcon onClick={() => navigate('/')} />;
}

export function LogoWithIcon({
  onClick,
  textColor = 'white',
}: { onClick?: () => void; textColor?: string } = {}) {
  return (
    <Flex
      align="center"
      h="100%"
      gap="xxs"
      onClick={onClick}
      style={{ cursor: onClick ? 'pointer' : 'auto' }}
    >
      <SquareLogo />
      <FullLogoIcon height={34} color={textColor} />
    </Flex>
  );
}
